// SmartSend part B — WhatsApp. Wire models for CONTRACT (11-CONTRACTS.md) §5 (API) and §6
// (state/thunks). Built against contract v1.20 (2026-09-10, body corrected 2026-09-13).
//
// PascalCase fields = server JSON keys (PulseemResponse.Data shapes) — no client renaming,
// the same rule Models/DataSources/SmartSend.ts and SendSearch.ts already follow.
//
// This file is NEW. `Models/DataSources/SmartSend.ts` is byte-identical (contract §1 "React
// בייט-זהים") and is imported from, never edited.

import { eSendChannel } from './SmartSend';

// ── GET WhatsApp/Availability (§5) ───────────────────────────────────────────────────────────
// ALWAYS 200, never 927/4xx (contract §5, T-20) — that is what lets the React side hide the
// whole add-on silently instead of rendering an error for an account that simply has no WhatsApp.
// 🔴 `TEMPLATES_UNREADABLE` IS A VALUE THE SERVER ALREADY SENDS (`SmartSendWhatsAppLogic.cs:279`)
// AND THE UNION DID NOT CARRY. The tile builds its text as `t(\`…blocked.${reason}\`)`, so a value
// missing from the union is not a type error at the call site — it is a RAW KEY NAME ON SCREEN.
// That is why the key list, this union and the mandatory group move in one edit.
export type WaBlockReason =
    | 'NOT_ONBOARDED' | 'NOT_VERIFIED' | 'PHONE_NOT_CONNECTED' | 'NO_APPROVED_TEMPLATES'
    | 'TEMPLATES_UNREADABLE' | 'CONFIG';

export interface WaFromNumber {
    FromNumber: string;
    // 🔴 `string`, not a number. `Dal/Models/DataSources/SmartSendWhatsAppModels.cs` declares
    // `public string Tier` — the tier is a NAME ("WHATSAPP_BASIC"), not a level. Nothing reads it
    // today, which is exactly why the wrong declaration was harmless and would have bitten the
    // first person who tried to compare or sort on it.
    Tier: string | null;
    // `int`, non-nullable on the server. Kept non-nullable here so the two sides say the same
    // thing; a wire NULL would be a server defect, not something to paper over.
    Limit: number;
}

export interface WaAvailability {
    Enabled: boolean;
    Ready: boolean;
    BlockReason: WaBlockReason | null;
    PlatformId: number | null;
    Verified: boolean;
    ApprovedTemplates: number;
    FromNumbers: WaFromNumber[];
}

// ── GET WhatsApp/Templates (§5) ──────────────────────────────────────────────────────────────
// `MediaUrl` / `MediaType` are v1.20 (R17): the server derives them from the `[Type]`
// (= TemplateContentTypes) column GetAllWhatsAppTemplates already returns — `$.media.media[0]`
// (card: `$.card.media[0]`) — and MediaType comes from the URL EXTENSION, never from the JSON's
// own `media_type` (Clal ids 4533/4534 declare "image" over a .pdf).
export interface WaTemplateItem {
    Id: number;
    TemplateId: string;              // ContentSid — the id every WhatsApp/* route speaks
    TemplateName: string;
    FriendlyTemplateName: string | null;
    Language: string | null;
    CategoryId: number | null;
    PlaceholderCount: number;
    IsContiguous: boolean;
    HasMedia: boolean;
    MediaUrl: string | null;
    MediaType: string | null;
    // `{{n}}` inside a URL button that the sender cannot fill (§5 / v1.4 T-04). Disables the
    // template in the picker exactly like a non-contiguous one.
    ButtonVarUnresolved: boolean;
    BodyText: string | null;
    ButtonsJson: string | null;
}

// ── GET/POST WhatsApp/Mapping/{id} (§5, RS1+RS2 of §3.2) ─────────────────────────────────────
export interface WaMapEntry {
    VariableIndex: number;
    DataSourceColumnID?: number | null;
    StaticValue?: string | null;
    SourceHeader?: string | null;
    ColumnInVersion?: boolean;
}

// 🔴 EXACTLY the nine fields of `WaMappingTemplate` in
// `Dal/Models/DataSources/SmartSendWhatsAppModels.cs`, in its order. It previously dropped three
// (`DataSourceID`, `DataSourceVersionID`, `VersionNumber`) and invented three
// (`WhatsAppTemplateID`, `PlaceholderCount`, `BodyText`).
//
// The invented `BodyText` was the one that mattered: two components fell back to
// `waMapping.Template.BodyText`, which is `undefined` on every real response, so the fallback
// they were written to provide silently resolved to `''` — a blank message bubble on a screen
// whose next button charges per message. tsc could not see it, because the type said the field
// was there. The ONLY source of a template body is `GET WhatsApp/Templates` → `BodyText`.
//
// `WhatsAppTemplateID` and `PlaceholderCount` are not lost either: both are top-level fields of
// `WaMappingResult` below, which is where the server actually sends them.
export interface WaTemplateSummary {
    DataSourceID: number | null;
    DataSourceVersionID: number | null;
    VersionNumber: number | null;
    TemplateName: string | null;
    FriendlyTemplateName: string | null;
    Language: string | null;
    CategoryId: number | null;
    TemplateStatusId: number | null;
    // Non-nullable `bool` on the server. With `TemplateStatusId` it is how the client tells
    // "the leg points at a template that is no longer approved" from "the list has not loaded
    // yet" — the two cases that used to collapse into the same empty bubble.
    TemplateIsDeleted: boolean;
}

export interface WaMappingResult {
    HasLeg: boolean;
    // EXPLICIT server column (v1.10 Z-01). Every consumer reads THIS — nobody derives it from
    // `WhatsAppCampaignID != null`, because a header deleted before sending leaves the pointer
    // set while there is no live header.
    HasHeader: boolean;
    // Header deleted AFTER sending (v1.11 AA-01) — screen state (5), read-only in full.
    SentHeaderDeleted: boolean;
    WhatsAppCampaignID: number | null;
    TestWhatsAppCampaignID: number | null;
    EmailCampaignStatus: number | null;      // = Campaigns.Status; retry only on 2 | 4
    WaRowsQueued: number | null;
    WaRows180: number | null;
    WaRows180AgeMin: number | null;
    // Age of the SEND ATTEMPT (wa.SendDate), seconds/60 — NOT the age of the rows. Mandatory
    // gate on BOTH disjuncts of receipt.stuck (v1.14 Q3-1).
    WaSendAgeMin: number | null;
    LastErrorStep: number | null;
    // "charged and not refunded" — read WITHOUT the LogicalDeleted gate (invariant 23 exemption,
    // v1.13 AC-03). Never infer "no charge" from a missing header.
    LastErrorCharged: boolean;
    WhatsAppTemplateID: string | null;
    FromNumber: string | null;
    PlaceholderCount: number | null;
    // v1.20 R17 — read-back of leg.MediaUrl. Without it every second mapping save would blank
    // the column, because POST treats a missing MediaUrl as "text template", not "leave alone".
    MediaUrl: string | null;
    LockedVersionID: number | null;
    HasCellIdentity: boolean;
    ResolvedRowsCell: number | null;
    // 🔴 v1.23 — THE CHANNEL BIT, and it is the whole feature. §2's column on
    // `CampaignWhatsAppLeg`, read back as an EXPLICIT RS1 column of `_GetMapping_Stage`
    // (§3.2) — and the contract says in the same breath why it must be explicit: "עמודה
    // חסרה נקראת בשרת כ-`false` בשקט … והפעם המחיר הוא שליחת מייל".
    //   no leg          ⇒ e-mail only  (`HasLeg=false`; this field is not read)
    //   SuppressEmail=0 ⇒ both
    //   SuppressEmail=1 ⇒ WhatsApp only
    // Non-nullable `bit NOT NULL DEFAULT(0)` on the server, so it is non-nullable here.
    SuppressEmail: boolean;
    WaCampaignStatus: number | null;
    TotalSendPlan: number | null;
    Template: WaTemplateSummary | null;
    Map: WaMapEntry[];
}

export interface WaSaveMappingRequest {
    WhatsAppTemplateID: string;
    FromNumber?: string | null;
    PlaceholderCount: number;
    MediaUrl?: string | null;
    // 🟡 v1.23 — DERIVED, NOT READ. §5's `POST WhatsApp/Mapping/{id}` body line was not
    // updated when v1.23 landed, so this one field name is the only thing in the package that
    // the contract does not state where a builder would look for it. It is not a guess: four
    // clauses pin it, and together they leave no other shape.
    //   · §2   — the column is `SuppressEmail`;
    //   · §3.1 — `@prm_SuppressEmail bit = NULL` is a parameter of `_SetMapping_Stage`;
    //   · §5   — `POST WhatsApp/Mapping/{id}` is the ONLY route that reaches that SP outside
    //            the TestSend path (§1 allows exactly 8 new `_Stage` SPs and none of them is a
    //            channel setter; `PUT Send` is declared "חתימה ללא שינוי"), so there is no
    //            other door;
    //   · §8   — invariant 26 lists "`SuppressEmail` כ-`bool` ולא `bool?` ב-DTO" as one of the
    //            four pre-deploy blockers, which only has a referent if a request DTO carries
    //            this field.
    // ⚠️ OPTIONAL, AND THE OPTIONALITY IS THE POINT — it mirrors `@prm_SuppressEmail = NULL`
    // ("not specified ⇒ `ISNULL(@prm, SuppressEmail)`"). It is sent ONLY when the operator has
    // actually changed the channel state; a save that omits it leaves the column alone. Always
    // sending the current value would re-create the very blocker §8 names, from the client
    // side: any save issued before `GET Mapping` seeded the flag would write 0 over a 1 and
    // send a mail the operator had switched off.
    // ⚠️ And it is the OPPOSITE convention to `MediaUrl` above, where NULL is a real value.
    // Both are right; do not "align" them. Flagged to the orchestrator for §5 to state it.
    SuppressEmail?: boolean;
    Map: { VariableIndex: number; DataSourceColumnID?: number | null; StaticValue?: string | null }[];
    // 09-channel-units — the source ON SCREEN. The server re-binds the WhatsApp unit's OWN mapping
    // row (Channel 3) when it is missing or on another source/version, so a source switch reaches
    // the WhatsApp channel through its own save. Omitted when unknown; the row is then left alone.
    DataSourceID?: number;
    DataSourceVersionID?: number;
    // 12-fix-wave 7.1 / 7.4(d) — sent (true) only after the operator confirmed the reset dialog.
    ConfirmReset?: boolean;
}

export interface WaSaveMappingResult {
    WhatsAppCampaignID: number | null;
    HasCellIdentity: boolean;
    WaCampaignStatus: number | null;
}

// ── GET MappingSuggestions/{id} · GET WhatsApp/MappingSuggestions/{id} (08-mapping-memory) ──────
// THE MAPPING MEMORY, one row per remembered variable: the source column NAME the operator chose
// the last time this (sub-account, channel, template, variable) was mapped, resolved by the server
// against the version being mapped now. EXACTLY the five fields of `SmartSendMappingSuggestion` in
// `Dal/Models/DataSources/SmartSendWhatsAppModels.cs`, PascalCase, no client renaming (plan v2
// §4.3 + §4.5). ONE type for both screens — the e-mail side imports it from here.
//
// 🔴 IT IS AN OFFER, NEVER A WRITE. The screens render it as the existing one-click chip; nothing
// applies a row on its own, and nothing applies N rows at once (`suggestMapping.ts:16-44`).
export interface SmartSendMappingSuggestion {
    // WhatsApp: the VariableIndex as a decimal string ('1', '2', …) — matched NUMERICALLY.
    // E-mail: the token exactly as it travels in SmartSendTokenMapRow.Token — matched AS-IS.
    VariableKey: string;
    // The remembered column NAME — never an id. Survives a re-upload; an id would not.
    SourceHeader: string;
    // Resolved in the requested version: TOP (1) … ORDER BY ColumnID, so on a duplicated header
    // the LOWEST ColumnID wins. NULL whenever `ColumnInVersion` is false.
    DataSourceColumnID: number | null;
    DisplayName: string | null;
    // false ⇒ the header is not in this version (or the version is not the caller's) ⇒ no bubble.
    ColumnInVersion: boolean;
}

// ── POST WhatsApp/Summary/{id} (§5 = RS1 of §3.3) ────────────────────────────────────────────
export interface WaSummaryResult {
    TotalRows: number;
    WithCell: number;
    BothIdentities: number;
    EmailOnly: number;
    PhoneOnly: number;
    NoIdentity: number;
    SkippedNoPhone: number;
    SkippedDuplicates: number;
    SkippedOptedOut: number;
    PendingQueued: number;
    Queued: number;
    MappedCount: number;
    PlaceholderCount: number;
    TemplateName: string | null;
    Language: string | null;
    CategoryId: number | null;
    HasHeader: boolean;
    SentHeaderDeleted: boolean;
    UnitCost: number | null;
    EstimatedCost: number | null;
    HasBalance: boolean;
    WhatsappTierID: number | null;
    WhatsappSmsLeft: number | null;
    NextAvailableTime: string | null;
}

// ── GET WhatsApp/Preview/{id} (§5) ───────────────────────────────────────────────────────────
export interface WaPreviewRow {
    VariableIndex: number;
    SourceHeader: string | null;
    Value: string | null;
    IsEmpty: boolean;
}

export interface WaPreviewResult {
    CandidateCount: number;
    RowID: number;
    RowOrdinal: number;
    RecipientName: string | null;
    ToNumberMasked: string | null;
    IsComplete: boolean;
    Values: { [index: string]: string };
    Rows: WaPreviewRow[];
}

// ── PUT WhatsApp/TestSend/{id} (§5) ──────────────────────────────────────────────────────────
export interface WaTestSendResultItem {
    Phone: string;
    Code: number;
    Message: string;
    NextAvailableTime?: string | null;
}

// ── GET WhatsApp/PendingLegs (§5 / §3.11, v1.8 X-01) ─────────────────────────────────────────
export interface WaPendingLeg {
    CampaignID: number;
    CampaignName: string;
    WhatsAppCampaignID: number | null;
    EmailCampaignStatus: number;
    LastErrorCharged: boolean;
    EmailSentAt: string | null;
}

// ── PUT Send/{id} → Data.WhatsApp, and PUT WhatsApp/Send/{id} (§5) ───────────────────────────
// 'DISABLED' is NOT a member (v1.5 U-04): when Enabled=false there is no Data.WhatsApp at all.
export type WaSendReason =
    | 'NO_LEG' | 'NO_CELL_IDENTITY' | 'NO_RECIPIENTS' | 'EMAIL_NOT_SENT' | 'INTERNAL_AFTER_DISPATCH'
    // PUT Send, third arm. The leg exists and channel 3 is off. Same spelling as the 409 `Message`
    // of PUT WhatsApp/Send, and as CHANNEL_NOT_ACTIVE in Models/DataSources/SmartSendChannels.ts:67.
    | 'CHANNEL_NOT_ACTIVE';

export interface WaSendOutcome {
    State: 'Skipped' | 'Queued' | 'Failed';
    Charged?: boolean;               // RS1 of the CATCH only — balance deducted and not refunded
    Live?: number;                   // rows still alive when the CATCH measured
    Reason?: WaSendReason;
    Code: number;
    Message: string;
    Queued: number;
    NextAvailableTime?: string | null;
    WhatsAppCampaignID: number | null;
}

// ── CONTRACT §4 — every `Message` the server can put on the wire ─────────────────────────────
// The i18n parity self-test asserts `send.whatsapp.error.*` ⊇ this union, in all three
// languages. Adding a code here without its three texts fails that test.
export type WaMessage =
    | 'WA_NO_BALANCE' | 'WA_TIER_LIMIT' | 'WA_NOT_VERIFIED' | 'WA_PHONE_NOT_CONNECTED'
    | 'WA_NO_RECIPIENTS' | 'WA_INTERNAL' | 'WA_ALREADY_SENT' | 'WA_NO_CELL_IDENTITY'
    | 'WA_EDIT_BLOCKED_DURING_SEND' | 'WA_COLUMN_NOT_IN_VERSION' | 'WA_TEMPLATE_NOT_APPROVED'
    | 'WA_VARIABLES_INCOMPLETE' | 'WA_HEADER_MISSING' | 'WA_EMAIL_LEG_MISSING'
    | 'WA_RECIPIENT_OPTED_OUT' | 'WA_ROW_NOT_FOUND' | 'WA_NO_LEG'
    | 'WA_SCHEDULE_NOT_SUPPORTED' | 'WA_EMAIL_NOT_SENT' | 'WA_PERMISSION'
    // 🔴 v1.21 — the two `GET WhatsApp/Preview` codes. They are the only §4 Messages WITHOUT a
    // `WA_` prefix, and that is deliberate: the owner adopted the LIVE precedent of the sibling
    // stepper (`SmartSendPreviewLogic.cs:416-431`) verbatim rather than minting new names, so the
    // two endpoints answer the same user action with the same words. Renaming them here would
    // break that on the wire.
    | 'NO_CANDIDATES' | 'NO_ROWS_AT_OFFSET'
    // 🔴 v1.23 — WhatsApp-only. Both arrive from the SERVER as a `Message`, and both describe a
    // refusal that only exists once the e-mail leg can be switched off:
    //   `WA_EMAIL_SUPPRESSED`         — `PUT Send` refuses a campaign whose e-mail is suppressed
    //                                   (§8's new invariant: it refuses BEFORE `SendCampaign`);
    //   `WA_SUPERVISOR_NOT_SUPPORTED` — the supervisor report has no WhatsApp-only form.
    // Names are the owner's, final as of 2026-09-14, and are carried word for word.
    | 'WA_EMAIL_SUPPRESSED' | 'WA_SUPERVISOR_NOT_SUPPORTED'
    // 🔴 v1.23b — `503`. The channel settings themselves could not be read, so the server does
    // not know which legs this campaign has: ZERO charge, ZERO send, on BOTH channels. Its text
    // says "לא נשלח דבר" outright, because the operator is standing in front of a charged send
    // button and the one thing they must not have to wonder about is a half-sent state.
    | 'WA_CONFIG_UNREADABLE'
    // 🔴 v1.23d — `409`, and it is the server refusing to be the only thing standing between a
    // direct call and a mail nobody asked for. `DELETE WhatsApp/Mapping/{id}` on a leg carrying
    // `SuppressEmail = 1` removes the ONE thing suppressing the e-mail, so it is answered with
    // this until the caller re-sends with `confirmSuppressedDelete=true`.
    // ⚠️ IT IS NOT A STATE THE OPERATOR SHOULD EVER SEE. The client's own dialog is what fills
    // that parameter; receiving this code means the client failed to, which is a bug on our
    // side — §8's "גידור בלקוח אינו מקיים את האינווריאנט", read from the other direction.
    | 'WA_SUPPRESS_CONFIRM_REQUIRED';

// Runtime twin of the union above — the self-test needs a value, not a type.
export const WA_MESSAGES: readonly WaMessage[] = [
    'WA_NO_BALANCE', 'WA_TIER_LIMIT', 'WA_NOT_VERIFIED', 'WA_PHONE_NOT_CONNECTED',
    'WA_NO_RECIPIENTS', 'WA_INTERNAL', 'WA_ALREADY_SENT', 'WA_NO_CELL_IDENTITY',
    'WA_EDIT_BLOCKED_DURING_SEND', 'WA_COLUMN_NOT_IN_VERSION', 'WA_TEMPLATE_NOT_APPROVED',
    'WA_VARIABLES_INCOMPLETE', 'WA_HEADER_MISSING', 'WA_EMAIL_LEG_MISSING',
    'WA_RECIPIENT_OPTED_OUT', 'WA_ROW_NOT_FOUND', 'WA_NO_LEG',
    'WA_SCHEDULE_NOT_SUPPORTED', 'WA_EMAIL_NOT_SENT', 'WA_PERMISSION',
    'NO_CANDIDATES', 'NO_ROWS_AT_OFFSET',
    'WA_EMAIL_SUPPRESSED', 'WA_SUPERVISOR_NOT_SUPPORTED',
    'WA_CONFIG_UNREADABLE', 'WA_SUPPRESS_CONFIRM_REQUIRED',
];

// 🔴 THE TWO 406 CODES ARE EMPTY STATES, NOT FAILURES. The request was understood and answered;
// there is simply nothing to show — no candidate rows at all (`NO_CANDIDATES`), or the candidate
// set shrank under a stepper that was already past its new end (`NO_ROWS_AT_OFFSET`). They must
// NOT be painted as an error, and they must CLEAR the row on screen: leaving the previous sample
// under a message that says there is none is the same stale-row defect in a new shape, on the
// surface that decides what a charged send will say.
export const PREVIEW_EMPTY_MESSAGES: readonly string[] = ['NO_CANDIDATES', 'NO_ROWS_AT_OFFSET'];

export const isPreviewEmptyCode = (code: string | null | undefined): boolean =>
    !!code && PREVIEW_EMPTY_MESSAGES.indexOf(code) > -1;

// ── screen-local shapes ──────────────────────────────────────────────────────────────────────
// The working variable map, keyed by VariableIndex — the WhatsApp twin of smartSend.tokenMap.
export interface WaVariableBinding {
    columnId: number | null;
    staticValue: string | null;
}

export type WaVariableMap = { [variableIndex: number]: WaVariableBinding };

export type WaSaveState = 'idle' | 'saving' | 'saved' | 'blocked' | 'error';

// Campaigns.Status values this package branches on. The full enum lives in
// Dal/Models/Enums/Campaign.cs; only the two that mean "the e-mail is out" are needed here.
export const EMAIL_SENT_STATUSES: readonly number[] = [2 /* Sending */, 4 /* Finished */];

// Re-exported so WhatsApp components never import two modules for one concept.
export { eSendChannel };
